import { useState } from 'react';
import { FlaskConical, MapPin, Loader2, Check, AlertTriangle, ArrowLeftRight } from 'lucide-react';
import AssignLabModal from './modals/AssignLabModal';
import { useGetMedicalLabQuery } from '@/store/api/apiSlice';
import type { AssignLabResponse } from '@/types';

interface Props {
    investigationId: string;
    variant: 'medical' | 'customer-success';
    // Employee location (employmentTown) used to seed the lab search.
    locationHint?: string | null | undefined;
    medicalLabId?: string | null | undefined;
    medicalLabAssignedAt?: string | null | undefined;
}

function formatDate(value?: string | null) {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function MedicalLabSection({
    investigationId,
    variant,
    locationHint,
    medicalLabId,
    medicalLabAssignedAt,
}: Props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [feedback, setFeedback] = useState<AssignLabResponse | null>(null);

    // Resolve the assigned lab's details (the investigation only embeds the id).
    const { data: lab, isLoading } = useGetMedicalLabQuery(medicalLabId || '', { skip: !medicalLabId });

    const handleAssigned = (result: AssignLabResponse) => {
        // The investigation detail refetches via tag invalidation; we keep the
        // response around to surface the email status to the user.
        setFeedback(result);
    };

    const locationLine = lab ? [lab.address, lab.city, lab.state].filter(Boolean).join(', ') : '';

    return (
        <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-3">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-50">
                    <FlaskConical className="h-4 w-4 text-[#FF6B35]" />
                </div>
                <div>
                    <h3 className="text-sm font-bold text-gray-900">Medical lab</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        The employee is emailed the lab and their test panel on assignment.
                    </p>
                </div>
            </div>

            {/* Post-assign feedback (email status) */}
            {feedback && (
                <div
                    className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 text-xs ${
                        feedback.emailSent
                            ? 'border-green-200 bg-green-50 text-green-700'
                            : 'border-amber-200 bg-amber-50 text-amber-800'
                    }`}
                >
                    {feedback.emailSent ? (
                        <Check className="w-4 h-4 shrink-0 mt-0.5" />
                    ) : (
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    )}
                    <span>
                        {feedback.emailSent
                            ? `Lab assigned — email sent to ${feedback.emailedTo}.`
                            : `Lab assigned, but the email to ${feedback.emailedTo} could not be sent. Please contact the employee directly.`}
                    </span>
                </div>
            )}

            {medicalLabId ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        {isLoading ? (
                            <span className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin" /> Loading lab…
                            </span>
                        ) : lab ? (
                            <>
                                <p className="text-sm font-semibold text-gray-900">{lab.name}</p>
                                {locationLine && (
                                    <p className="text-xs text-gray-500 mt-0.5 flex items-start gap-1">
                                        <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                                        {locationLine}
                                    </p>
                                )}
                                {medicalLabAssignedAt && (
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        Assigned on {formatDate(medicalLabAssignedAt)}
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-gray-500">Lab assigned.</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        className="shrink-0 border border-gray-200 hover:bg-white text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                    >
                        <ArrowLeftRight className="w-3.5 h-3.5" />
                        Reassign
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="self-start bg-[#FF6B35] hover:bg-[#FF5722] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                >
                    <FlaskConical className="w-4 h-4" />
                    Assign medical lab
                </button>
            )}

            <AssignLabModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                investigationId={investigationId}
                variant={variant}
                locationHint={locationHint}
                currentLabId={medicalLabId}
                onAssigned={handleAssigned}
            />
        </div>
    );
}
